<script lang="ts" setup>
import { AbcFloatingContainer } from "@ordbogen-npm/abc-dsk";
import { computed } from "vue";

import TgTooltip from "@/abc-dsk/components/Tooltip/TgTooltip.vue";
import { useArticleFont } from "@/common/composables/use-article-font.composable";
import { buildGenderHeader } from "@/components/article/dictsys/inflectionSchemes/labels";
import { mapGrammaticalLabelsToLevel } from "@/components/article/dictsys/level-mapping";
import IconHelp from "@/components/icons/IconHelp.vue";
import { useSettingsStore } from "@/stores";
import type { Level } from "@/types/dictionary";
import type { WordClass, WordClassLabelSet } from "@/types/inflection-trainer.type";

import PlaceholderWord from "./PlaceholderWord.vue";

type Gender = "masculine" | "feminine" | "neutral";

const props = defineProps<{
  lemma?: string;
  wordClass: WordClass;
  wordClassLabels: WordClassLabelSet;
  gender?: Gender | null;
}>();

const wordClassDescriptions = {
  noun: "Substantiver er ord, der betegner ting, levende væsener, steder og begreber. På tysk kan de bøjes i køn, tal, bestemthed og kasus. Substantiver kaldes også navneord.",
  verb: "Verber er ord, der fortæller, at nogen gør noget, eller at nogen eller noget er i en tilstand. På tysk kan de bøjes i tid, tal og person. Verber kaldes også udsagnsord.",
  adjective:
    "Adjektiver er ord, der lægger sig til substantiver og beskriver deres egenskaber. På tysk kan de bøjes i køn, tal, bestemthed, grad og kasus. Adjektiver kaldes også tillægsord."
};

const settingsStore = useSettingsStore();

const wordClassLevel = computed<Level>(() => settingsStore.activeWordClassLevel);

const genderLabel = computed(() =>
  props.gender ? buildGenderHeader(wordClassLevel.value, props.gender) : ""
);

const wordClassLabel = computed(() =>
  mapGrammaticalLabelsToLevel(
    wordClassLevel.value,
    props.wordClassLabels[props.wordClass].danish,
    props.wordClassLabels[props.wordClass].latin
  )
);

const wordClassDescription = computed(() => wordClassDescriptions[props.wordClass]);

const { articleFontClass } = useArticleFont();
</script>

<template>
  <AbcFloatingContainer class="inflection-scheme-article" :class="articleFontClass">
    <div class="inflection-scheme-article__header">
      <TgTooltip :text="wordClassDescription">
        <IconHelp width="22" height="22" />
      </TgTooltip>
      <PlaceholderWord v-if="!lemma" :translucent="true" color="white" width="95px" />
      <span v-else class="inflection-scheme-article__lemma">{{ lemma }}</span>
      <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
      <span class="inflection-scheme-article__word-class"
        >{{ wordClassLabel }}<span v-if="genderLabel">,</span></span
      >
      <span class="inflection-scheme-article__gender">{{ genderLabel }}</span>
    </div>
    <div class="inflection-scheme-article__content">
      <slot />
    </div>
  </AbcFloatingContainer>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.inflection-scheme-article {
  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgb(@theme-colors[@primary][base]);
    color: @colors[@white];

    #media-breakpoint-up(md, {
      gap: 12px;
      padding: 12px 64px;
    });
  }

  &__lemma {
    font-size: 1rem;
    font-weight: 700;
    word-wrap: break-word;
    hyphens: auto;
  }

  &__word-class,
  &__gender {
    font-size: 1rem;
    font-style: italic;
    font-weight: 400;
    text-transform: lowercase;
  }

  &__content {
    padding: 22px 16px;

    #media-breakpoint-up(md, {
      padding: 24px 64px 48px;
    });
  }
}
</style>
